import React from 'react';
import { cn } from '../lib/utils';
import type { LucideIcon } from 'lucide-react';

interface PremiumPageHeaderProps {
  title: string;
  subtitle?: string | React.ReactNode;
  icon: LucideIcon;
  /** Custom gradient classes for background elements */
  gradientClassName?: string;
  /** Custom classes for the icon container backdrop */
  iconContainerClassName?: string;
  /** Custom classes for the icon itself */
  iconClassName?: string;
  children?: React.ReactNode;
  className?: string;
}

export const PremiumPageHeader: React.FC<PremiumPageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  gradientClassName = 'from-indigo-500/10 via-purple-500/5 to-pink-500/5',
  iconContainerClassName = 'bg-indigo-500/15 border border-indigo-200/30 dark:border-indigo-700/30',
  iconClassName = 'text-indigo-600 dark:text-indigo-400',
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl bg-gradient-to-br border border-indigo-200/30 dark:border-indigo-800/30 p-6 md:p-8 transition-all duration-300',
        gradientClassName,
        className
      )}
    >
      {/* Glow Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          {/* Icon Container with subtle background */}
          <div
            className={cn(
              'p-2.5 md:p-3 rounded-xl flex items-center justify-center shrink-0 shadow-sm transition-transform duration-300 hover:scale-110',
              iconContainerClassName
            )}
          >
            <Icon className={cn('w-6 h-6 md:w-7 md:h-7', iconClassName)} />
          </div>

          <div className="space-y-0.5">
            <h1 className="text-2xl md:text-3xl font-black text-foreground tracking-tight leading-tight">
              {title}
            </h1>
            {subtitle && (
              <div className="text-sm text-muted-foreground font-medium mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0 empty:hidden">{children}</div>
      </div>
    </div>
  );
};
